/**
 * HelpDrawerComponents — vue de lecture d'article + états (chargement / erreur).
 *
 * La liste d'articles et la recherche sont désormais dans le CHAMP UNIFIÉ
 * (`HelpSearchView`) ; l'ancienne exclusion `showSearch={!enableAssistant}` a
 * disparu avec elle (recherche et IA cohabitent — HELP-DRAWER §3).
 */

import { useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { HelpCircle, Loader2 } from 'lucide-react'
import { MarkdownRenderer } from './MarkdownRendererLazy'
import { DrawerTableOfContents } from './DrawerTableOfContents'
import { type HelpArticle } from '../../hooks/useHelp'
import { slugifyHeading } from '../../lib/toc'

export function ArticleView({
    article,
    scrollToSection = null,
}: {
    article: HelpArticle
    /** Titre de section vers laquelle scroller à l'ouverture (deep-link depuis une
        carte source de l'assistant). `null` = haut de l'article. */
    scrollToSection?: string | null
}) {
    // Retirer le premier H1 (titre) du contenu car il est déjà dans le header
    const contentWithoutTitle = article.content.replace(/^#\s+.+\n+/, '')

    const rootRef = useRef<HTMLDivElement>(null)
    // Le conteneur scrollable est le viewport interne de la ScrollArea du drawer.
    const getContainer = useCallback(
        () => rootRef.current?.closest('[data-radix-scroll-area-viewport]') as HTMLElement | null,
        [],
    )

    // Deep-link : à l'ouverture depuis une source de l'assistant, scroller à la
    // SECTION gagnante. L'id du titre = slugifyHeading(texte) (même fonction que
    // HeadingRenderer) ; `scroll-mt-20` gère l'offset. rAF pour laisser le markdown
    // se peindre avant de scroller.
    useEffect(() => {
        if (!scrollToSection) return
        const id = slugifyHeading(scrollToSection)
        const raf = requestAnimationFrame(() => {
            document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        })
        return () => cancelAnimationFrame(raf)
    }, [scrollToSection, article.slug])

    return (
        <div ref={rootRef} className="w-full min-w-0">
            <DrawerTableOfContents content={contentWithoutTitle} getContainer={getContainer} />
            <MarkdownRenderer
                content={contentWithoutTitle}
                className="prose-sm prose-headings:text-foreground prose-p:text-muted-foreground"
            />
        </div>
    )
}

export function LoadingState() {
    const { t } = useTranslation()

    return (
        <div className="flex flex-col items-center justify-center py-16 bg-card rounded-xl shadow-md">
            <div className="h-14 w-14 rounded-full bg-accent flex items-center justify-center mb-4">
                <Loader2 className="h-7 w-7 animate-spin text-primary motion-reduce:animate-none" />
            </div>
            <p className="text-muted-foreground font-medium">{t('help.loading')}</p>
        </div>
    )
}

export function ErrorState({ message }: { message: string }) {
    const { t } = useTranslation()

    return (
        <div className="flex flex-col items-center justify-center py-16 bg-card rounded-xl shadow-md">
            <div className="h-16 w-16 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
                <HelpCircle className="h-8 w-8 text-destructive" />
            </div>
            <p className="text-center font-semibold text-destructive">{t('help.error')}</p>
            <p className="text-sm mt-2 text-muted-foreground">{message}</p>
        </div>
    )
}
